import React, { useState, useRef, useEffect } from 'react';
import { Edit2 } from 'lucide-react';

const InlineEditField = ({ value, onSave, placeholder, multiline, colors, textStyle = {}, inputStyle = {} }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const [isHovered, setIsHovered] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.setSelectionRange) {
        const len = (localValue || '').length;
        inputRef.current.setSelectionRange(len, len);
      }
    }
  }, [isEditing]);

  const handleSave = async () => {
    const trimmed = (localValue || '').trim();
    if (trimmed === (value || '').trim()) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed);
    } finally {
      setSaving(false);
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setLocalValue(value);
      setIsEditing(false);
    }
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Enter' && e.ctrlKey && multiline) {
      e.preventDefault();
      handleSave();
    }
  };

  const baseInputStyle = {
    width: '100%',
    backgroundColor: colors.bgSecondary,
    color: colors.textPrimary,
    border: `1px solid ${colors.accentPrimary}60`,
    borderRadius: '8px',
    padding: '8px 12px',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
    ...inputStyle,
  };

  if (isEditing) {
    const Tag = multiline ? 'textarea' : 'input';
    return (
      <Tag
        ref={inputRef}
        value={localValue || ''}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={saving}
        rows={multiline ? 3 : undefined}
        style={{
          ...baseInputStyle,
          fontSize: textStyle.fontSize || '14px',
          fontWeight: textStyle.fontWeight || 400,
          resize: multiline ? 'vertical' : undefined,
          opacity: saving ? 0.6 : 1,
        }}
      />
    );
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        cursor: 'pointer',
        borderRadius: '6px',
        padding: '2px 4px',
        margin: '-2px -4px',
        transition: 'background-color 0.15s',
        backgroundColor: isHovered ? `${colors.textSecondary}10` : 'transparent',
        minHeight: '24px',
      }}
    >
      <span style={{ ...textStyle, flex: 1 }}>
        {value || <span style={{ color: colors.textTertiary, fontStyle: 'italic' }}>{placeholder}</span>}
      </span>
      <Edit2
        size={12}
        color={colors.textTertiary}
        style={{ opacity: isHovered ? 0.8 : 0, transition: 'opacity 0.15s', flexShrink: 0 }}
      />
    </div>
  );
};

export default InlineEditField;
